import './CaseResolutionPackageItems.scss';

import { Collapse, CollapseProps } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import { SHIPPING_ITEM_REFERENCE } from '@/model/Logistic/Shipment/ShipmentReference';
import { ServiceOrderItem } from '@/model/ServiceOrder/ServiceOrderItem';

import CaseResolutionItemCard from './CaseResolutionItemCard';
import useCaseResolutionItems from './useCaseResolutionItems';

interface CaseResolutionPackageItemsProps {
  packageItems: PackageItem[];
  serviceOrderItems: ServiceOrderItem[];
  refetch: () => void;
}

const CaseResolutionPackageItems = (props: CaseResolutionPackageItemsProps) => {
  const { t } = useTranslation();
  const { packageItems, serviceOrderItems, refetch } = props;
  const { caseResolutionItems } = useCaseResolutionItems(packageItems, serviceOrderItems);
  const caseResolutionGroups: CollapseProps['items'] = useMemo(() => {
    const groups: CollapseProps['items'] = [];
    const { itemComparisons, nonReturnItems, receptionForRevisionComparisons } =
      caseResolutionItems;
    const evaluationItems = itemComparisons.filter(
      ic => ic.shipmentReference == SHIPPING_ITEM_REFERENCE.SUJETO_PERITAJE,
    );
    const preventiveItems = itemComparisons.filter(
      ic => ic.shipmentReference == SHIPPING_ITEM_REFERENCE.PREVENTIVO,
    );
    const sparePartsItems = nonReturnItems.filter(
      i => i.item_shipped_reference == SHIPPING_ITEM_REFERENCE.VENTA_REPUESTO,
    );
    const nonReturn = nonReturnItems.filter(
      i => i.item_shipped_reference == SHIPPING_ITEM_REFERENCE.SIN_DEVOLUCION,
    );

    if (evaluationItems.length > 0) {
      groups.push({
        key: 'item-evaluations',
        label: `${t('Logística sujetos a peritaje')} (${evaluationItems.length})`,
        children: (
          <>
            {evaluationItems.map(comparison => (
              <CaseResolutionItemCard
                key={comparison.id}
                roundTripComparison={comparison}
                refetch={refetch}
              />
            ))}
          </>
        ),
      });
    }

    if (preventiveItems.length > 0) {
      groups.push({
        key: 'item-preventive',
        label: `${t('Logística preventivos')} (${preventiveItems.length})`,
        children: (
          <>
            {preventiveItems.map(comparison => (
              <CaseResolutionItemCard
                key={comparison.id}
                roundTripComparison={comparison}
                refetch={refetch}
              />
            ))}
          </>
        ),
      });
    }

    if (receptionForRevisionComparisons.length > 0) {
      groups.push({
        key: 'reception-for-revision',
        label: `${t('Recepciones por solicitudes reparación o revisión')} (${receptionForRevisionComparisons.length})`,
        children: (
          <>
            {receptionForRevisionComparisons.map(comparison => (
              <CaseResolutionItemCard
                key={comparison.id}
                receptionForRevision={comparison}
                refetch={refetch}
              />
            ))}
          </>
        ),
      });
    }

    if (sparePartsItems.length > 0) {
      groups.push({
        key: 'shipment-spare-parts',
        label: `${t('Envío por venta de repuesto')} (${sparePartsItems.length})`,
        children: (
          <>
            {sparePartsItems.map(item => (
              <CaseResolutionItemCard key={item.id} nonReturnItem={item} refetch={refetch} />
            ))}
          </>
        ),
      });
    }

    if (nonReturn.length > 0) {
      groups.push({
        key: 'shipment-no-return',
        label: `${t('Envío sin devolución')} (${nonReturn.length})`,
        children: (
          <>
            {nonReturn.map(item => (
              <CaseResolutionItemCard key={item.id} nonReturnItem={item} refetch={refetch} />
            ))}
          </>
        ),
      });
    }

    return groups;
  }, [caseResolutionItems, refetch, t]);

  return (
    <div className="CaseResolutionPackageItems">
      {caseResolutionGroups.length > 0 && (
        <div className="CaseResolutionShowTitle">{t('Elementos involucrados')}</div>
      )}

      <Collapse ghost items={[...caseResolutionGroups]} />
    </div>
  );
};

export default CaseResolutionPackageItems;
